import { IdentityUserAri } from '@atlassian/ari/identity';
import { JiraProjectAri } from '@atlassian/ari/jira';
import type { JiraFilters } from '@atlassian/search-client/query-generator';
import { isQueryLikeWorkItemKey } from '@atlassian/search-common/generate-search-url';

import type { GenerateSearchUrlType } from '../../../common/ui/app-context';
import { type QueryParams } from '../../../common/utils/query-params/types';

export const buildJql = ({
	query,
	projectARIs,
	accountARIs,
}: {
	query: string;
	projectARIs: string[];
	accountARIs: string[];
}): string => {
	let text: string | undefined;
	if (query) {
		// If the query looks like an issue key, use issueKey instead of textfields
		if (isQueryLikeWorkItemKey(query)) {
			text = `issuekey ~ "${encodeURIComponent(query)}*"`;
		} else {
			text = `textfields ~ "${encodeURIComponent(query)}*"`;
		}
	}

	const projectIds = projectARIs.map(getProjectIdFromProjectAri).filter(Boolean);
	const projectJQL =
		projectARIs.length > 0 ? `project IN (${encodeURIComponent(projectIds.join(','))})` : undefined;

	const accountIds = accountARIs.map(getAccountIdFromUserAri).filter(Boolean);
	const assigneeClause =
		accountIds.length > 0 ? `assignee IN (${encodeURIComponent(accountIds.join(','))})` : undefined;

	return [text, projectJQL, assigneeClause].filter(Boolean).join(' AND ');
};

function getProjectIdFromProjectAri(projectAri?: string): string | undefined {
	try {
		return projectAri ? JiraProjectAri.parse(projectAri).projectId : undefined;
	} catch {
		return undefined;
	}
}

function getAccountIdFromUserAri(userAri?: string): string | undefined {
	try {
		return userAri ? IdentityUserAri.parse(userAri).userId : undefined;
	} catch {
		return undefined;
	}
}

export const getWorkItemPageUrl = ({
	query,
	jiraFilters,
	generateSearchUrl,
	queryParams = {},
	issueKeyLookup,
	isQueryJql = false,
}: {
	query: string;
	jiraFilters: JiraFilters;
	generateSearchUrl: GenerateSearchUrlType;
	queryParams?: QueryParams;
	issueKeyLookup?: boolean;
	isQueryJql?: boolean;
}): string => {
	const trimmedQuery = query.trim();
	let jql;

	if (isQueryJql) {
		jql = trimmedQuery;
	} else if (
		trimmedQuery !== '' &&
		(jiraFilters.issueFilter.projectARIs.length !== 0 ||
			jiraFilters.issueFilter.assigneeARIs.length !== 0)
	) {
		/* If query is empty we don't need to build jql as it will be ignored by generateSearchUrl
		 ** Otherwise, we pass the JQL to be redirected to work items page */
		jql = buildJql({
			query: trimmedQuery,
			accountARIs: jiraFilters.issueFilter.assigneeARIs,
			projectARIs: jiraFilters.issueFilter.projectARIs,
		});
	}

	/* generateSearchUrl takes care of navigating to a signle work item page in case the query is a work item key
	 * Otherwise, it'll take the user to work items page.
	 * */
	return generateSearchUrl(
		{
			...queryParams,
			text: query,
			...(jql && { jql }),
		},
		true,
		undefined,
		undefined,
		issueKeyLookup,
	);
};

// Must match the real submit destination exactly (no extra params) or the
// prefetched route won't be reused on navigation.
export const getJiraSearchUrl = (query: string): string =>
	`/jira/search?searchString=${encodeURIComponent(query)}`;

// Prefetch BOTH the direct JQL destination and the `/jira/search` intermediary:
// a deliberate over-fetch so Enter is instant whether the submit resolves to the
// JQL page (filtered) or the intermediary (unfiltered / before results return).
export const getWorkItemPrefetchUrls = (params: {
	query: string;
	jiraFilters: JiraFilters;
	generateSearchUrl: GenerateSearchUrlType;
	queryParams?: QueryParams;
	// Mirror the submit-time JQL detection (`isJqlQueryDetected`) so the prefetched
	// URL matches the destination Enter resolves to; otherwise typed-JQL queries
	// prefetch a different URL than they navigate to and the prefetch is wasted.
	isQueryJql?: boolean;
}): string[] => {
	const trimmedQuery = params.query.trim();
	if (trimmedQuery === '') {
		return [];
	}
	// Issue-key queries submit to /browse/<KEY> (resolved by backend quicksearch),
	// which can't be prewarmed; prefetching only adds contention to the issue-view load.
	if (isQueryLikeWorkItemKey(trimmedQuery)) {
		return [];
	}
	const jqlUrl = getWorkItemPageUrl({ ...params, query: trimmedQuery, issueKeyLookup: false });
	return [jqlUrl, getJiraSearchUrl(trimmedQuery)];
};
